import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, uitvoeren } from './client.ts';
import { useApiMutatie } from './mutaties.ts';
import type { Matchbeoordeling, MatchFilter } from './typen.ts';

export const matchSleutels = {
  alle: ['matches'] as const,
  voorVacature: (vacatureId: string, filter: MatchFilter) => ['matches', 'vacature', vacatureId, filter] as const,
  voorKandidaat: (kandidaatId: string, filter: MatchFilter) => ['matches', 'kandidaat', kandidaatId, filter] as const,
};

/**
 * Somt de matches voor een vacature op, hoogste score eerst.
 * @param vacatureId Het id van de vacature.
 * @param filter Engineversie, minimale score, beoordeling en paginering.
 * @returns De query met de matches.
 */
export function useMatchesVoorVacature(vacatureId: string | undefined, filter: MatchFilter = {}) {
  return useQuery({
    queryKey: matchSleutels.voorVacature(vacatureId ?? '', filter),
    queryFn: () =>
      uitvoeren(
        api.GET('/api/v1/vacatures/{id}/matches', { params: { path: { id: vacatureId ?? '' }, query: filter } }),
      ),
    enabled: Boolean(vacatureId),
    placeholderData: keepPreviousData,
  });
}

/**
 * Somt de matches voor een kandidaat op, hoogste score eerst.
 * @param kandidaatId Het id van de kandidaat.
 * @param filter Engineversie, minimale score, beoordeling en paginering.
 * @returns De query met de matches.
 */
export function useMatchesVoorKandidaat(kandidaatId: string | undefined, filter: MatchFilter = {}) {
  return useQuery({
    queryKey: matchSleutels.voorKandidaat(kandidaatId ?? '', filter),
    queryFn: () =>
      uitvoeren(
        api.GET('/api/v1/kandidaten/{id}/matches', { params: { path: { id: kandidaatId ?? '' }, query: filter } }),
      ),
    enabled: Boolean(kandidaatId),
    placeholderData: keepPreviousData,
  });
}

/**
 * De mutaties op matches: beoordelen en verouderde matches opruimen.
 * @returns De mutaties.
 */
export function useMatchMutaties() {
  return {
    beoordeel: useApiMutatie(
      ({ id, beoordeling }: { id: string; beoordeling: Matchbeoordeling }) =>
        uitvoeren(api.PUT('/api/v1/matches/{id}/beoordeling', { params: { path: { id } }, body: { beoordeling } })),
      () => [matchSleutels.alle],
    ),
    ruimOp: useApiMutatie(
      (huidigeEngineversie: string) =>
        uitvoeren(api.POST('/api/v1/matches/opruiming', { body: { huidigeEngineversie } })),
      () => [matchSleutels.alle],
    ),
  };
}
